/**
 * Sync Engine — processes the offline sync queue when connectivity is available.
 * 
 * - FIFO per job
 * - Atomic ordering: photos/signatures upload before appraisal completion
 * - Exponential backoff on failure (never gives up permanently)
 * - Auto-triggers on network reconnect + foreground resume + connection quality change
 * - Last-write-wins: no conflict detection, uploads always proceed
 * - Singleton; safe to call start() multiple times
 * - Native: uses Capacitor Network plugin for reliable reconnect detection
 */

import { syncQueue, appState, isRecoverableDbError, describeDbError, shouldRunBackgroundStorageWork, resumeStorageRecovery, type SyncQueueItem } from "./offlineDb";
import { captureError, captureEvent } from "@/lib/observability";
import { invokeEdgeFunction } from "./invokeEdgeFunction";
import {
  getCompletionFallbacks,
  removeCompletionFallback,
} from "./completionFallbackStore";
import { isKnownOffline } from "./connectivity";
import { logOfflineEvent } from "./offlineTelemetry";

/** Stage telemetry — one component so a whole completion can be traced. */
function syncEvent(stage: string, metadata: Record<string, unknown> = {}, isError = false) {
  try {
    captureEvent({
      kind: isError ? "error" : "performance",
      component: "sync.completion",
      message: stage,
      metadata,
    });
  } catch { /* never let telemetry break sync */ }
  console.log(`[SyncEngine] ${stage}`, metadata);
}


export type SyncStatus = "idle" | "syncing";
type SyncListener = (status: SyncStatus, pending: number) => void;

let status: SyncStatus = "idle";
let processing = false;
let intervalId: ReturnType<typeof setInterval> | null = null;
let visibilityCleanup: (() => void) | null = null;
const listeners = new Set<SyncListener>();

// Track last known connection quality for smart retry
let lastConnectionQuality: "none" | "poor" | "good" = "none";

// ─── Public API ──────────────────────────────────────────────

export function getSyncStatus(): SyncStatus {
  return status;
}

export function onSyncStatusChange(fn: SyncListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(pending: number) {
  for (const fn of listeners) {
    try { fn(status, pending); } catch { /* ignore */ }
  }
}

/** Start the sync engine (call once on app boot) */
export function startSyncEngine() {
  if (intervalId) return;

  // One-time: clear any stale conflict data from old builds
  clearLegacyConflictStore();

  // Process queue every 10 seconds
  intervalId = setInterval(runIntervalPass, 10_000);
  // Listen for online events (web + Capacitor custom event)
  window.addEventListener("online", onOnline);
  window.addEventListener("network-status-change", onNetworkChange as EventListener);

  // Resume sync when app comes back to foreground (native)
  const handleVisibility = () => {
    if (document.visibilityState === "visible") {
      console.log("[SyncEngine] App foregrounded, flushing queue…");
      resumeStorageRecovery("foreground");
      processQueue();
      // Upload tracking hotfix: dormant by default, so skip reconciliation unless explicitly enabled
      import("./uploadJobTracker")
        .then((m) => {
          if (m.isUploadTrackingEnabled()) {
            void m.reconcileUploadJobs();
          }
        })
        .catch(() => {});
    }
  };
  document.addEventListener("visibilitychange", handleVisibility);
  visibilityCleanup = () => document.removeEventListener("visibilitychange", handleVisibility);

  console.log("[SyncEngine] Started");
}

/** Stop the sync engine (cleanup) */
export function stopSyncEngine() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  window.removeEventListener("online", onOnline);
  window.removeEventListener("network-status-change", onNetworkChange as EventListener);
  visibilityCleanup?.();
  visibilityCleanup = null;
  console.log("[SyncEngine] Stopped");
}

/** Manually trigger a sync flush */
export async function flushSync(): Promise<void> {
  await processQueue();
}

// ─── Internals ───────────────────────────────────────────────

function onOnline() {
  console.log("[SyncEngine] Online detected, flushing queue…");
  syncEvent("connectivity_restored", { source: "online-event" });
  resumeStorageRecovery("online");
  processQueue();
}

/** Smart retry: detect connection quality improvement */
function onNetworkChange(event: CustomEvent) {
  const detail = event.detail;
  if (!detail) return;
  
  const connected = detail.connected;
  const connType = detail.connectionType || "";
  
  let quality: "none" | "poor" | "good" = "none";
  if (connected) {
    quality = connType === "2g" ? "poor" : "good";
  }
  
  if (quality !== "none" && lastConnectionQuality === "none") {
    syncEvent("connectivity_restored", { source: detail.source || "network-status-change", quality });
    processQueue();
  } else if (quality === "good" && lastConnectionQuality !== "good") {
    console.log(`[SyncEngine] Connection improved (${lastConnectionQuality} → good), flushing…`);
    syncEvent("connectivity_restored", { source: detail.source || "quality-improved", quality });
    processQueue();
  }

  
  lastConnectionQuality = quality;
}

/**
 * Sort queue items so photos/signatures come before completion actions.
 * This ensures atomic upload ordering.
 */
function sortForAtomicUpload<T extends Pick<SyncQueueItem, "job_id" | "type" | "created_at">>(items: T[]): T[] {
  const priority: Record<string, number> = {
    UPLOAD_PHOTO: 0,
    UPLOAD_SIGNATURE: 1,
    UPSERT_PART_INSPECTION: 2,
    UPSERT_POC: 3,
    UPSERT_POD: 4,
    COMPLETE_POC: 5,
    COMPLETE_POD: 6,
  };

  
  return [...items].sort((a, b) => {
    if (a.job_id !== b.job_id) {
      return a.created_at - b.created_at;
    }
    const pa = priority[a.type] ?? 99;
    const pb = priority[b.type] ?? 99;
    if (pa !== pb) return pa - pb;
    return a.created_at - b.created_at;
  });
}

/**
 * Max times a completion action can be deferred waiting for asset uploads.
 * After this, the completion proceeds anyway — the edge function handles
 * the job transition even if some photos/signatures haven't arrived yet.
 */
const MAX_ASSET_DEFER_ATTEMPTS = 3;

/**
 * Stall watchdog: last-resort recovery only. Normal timeouts are handled by
 * the per-item deadline below, which releases the pass (and therefore the
 * processing lock) immediately — the watchdog exists purely for the
 * pathological case where a promise never settles at all.
 */
const PROCESSING_STALL_MS = 3 * 60 * 1000;
let processingStartedAt = 0;
/**
 * Pass ownership. The stall watchdog may hand the lock to a new pass while the
 * old one is still awaiting; the old pass must then stop before its next item
 * and must not release the new pass's lock in its `finally`.
 */
let passGeneration = 0;

/**
 * Hard per-item deadline. Slightly longer than the largest edge-function
 * timeout (120s for photo/signature uploads) so the AbortController fires
 * first and produces the normal, retryable "Request timed out" error. If a
 * platform ever fails to honour the abort, this guarantees the loop still
 * advances instead of holding the lock until the watchdog.
 */
const ITEM_DEADLINE_MS = 135_000;

function withItemDeadline<T>(promise: Promise<T>, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    promise,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error(`${label} exceeded ${Math.round(ITEM_DEADLINE_MS / 1000)}s deadline`)),
        ITEM_DEADLINE_MS
      );
    }),
  ]).finally(() => clearTimeout(timer)) as Promise<T>;
}


/**
 * Re-queue completions that were mirrored to localStorage because the
 * IndexedDB write could not be verified. Deterministic ids mean a completion
 * that is already queued is never duplicated.
 */
async function rehydrateCompletionFallbacks(): Promise<void> {
  const fallbacks = getCompletionFallbacks();
  if (fallbacks.length === 0) return;

  let existing: SyncQueueItem[] = [];
  try {
    existing = await syncQueue.getAll();
  } catch {
    existing = [];
  }

  for (const fb of fallbacks) {
    const alreadyQueued = existing.some((i) => i.job_id === fb.jobId && i.type === fb.type);
    if (alreadyQueued) {
      removeCompletionFallback(fb.id);
      continue;
    }
    try {
      await syncQueue.enqueue({
        job_id: fb.jobId,
        company_id: fb.companyId,
        type: fb.type,
        payload_json: fb.payload_json,
        file_data: null,
      });
      removeCompletionFallback(fb.id);
      syncEvent("completion_rehydrated", { jobId: fb.jobId, type: fb.type });
    } catch (err: any) {
      // IndexedDB still unusable — send the completion straight to the server.
      try {
        const fn = fb.type === "COMPLETE_POC" ? "mobile-poc-complete" : "mobile-pod-complete";
        await callEdgeFunction(fn, JSON.parse(fb.payload_json));
        removeCompletionFallback(fb.id);
        syncEvent("completion_sent_direct", { jobId: fb.jobId, type: fb.type });
      } catch (sendErr: any) {
        syncEvent("completion_rehydrate_failed", {
          jobId: fb.jobId,
          type: fb.type,
          queueError: err?.message,
          sendError: sendErr?.message,
        }, true);
      }
    }
  }
}

/** Dashboard refresh after a completion has been accepted by the server. */
async function refreshAfterCompletion(jobId: string): Promise<void> {
  try {
    const { queryClient } = await import("./queryClient");
    await queryClient.invalidateQueries();
    window.dispatchEvent(new CustomEvent("offline-completion-synced", { detail: { jobId } }));
    syncEvent("dashboard_refreshed", { jobId });
    logOfflineEvent("dashboard_refresh_triggered", { jobId, subject: "completion_synced" });
  } catch (err: any) {
    syncEvent("dashboard_refresh_failed", { jobId, error: err?.message }, true);
  }
}

// ─── Local-storage failure backoff ───────────────────────────
// When the phone's local database keeps failing (e.g. iOS WebKit
// "Attempt to get records from database without an in-progress transaction"),
// the 10-second timer backs off instead of hammering IndexedDB. Online,
// foreground and manual flushes still attempt recovery immediately.
// Queue contents are never touched by this path.
const STORAGE_BACKOFF_BASE_MS = 10_000;
const STORAGE_BACKOFF_MAX_MS = 5 * 60 * 1000;
let storageFailureStreak = 0;
let storageBackoffUntil = 0;

/** Exposed for tests. */
export function getStorageBackoffState() {
  return { streak: storageFailureStreak, backoffUntil: storageBackoffUntil };
}
/** Exposed for tests. */
export function resetStorageBackoffForTests() {
  storageFailureStreak = 0;
  storageBackoffUntil = 0;
}

export function storageBackoffDelay(streak: number): number {
  if (streak <= 0) return 0;
  return Math.min(STORAGE_BACKOFF_BASE_MS * Math.pow(2, streak - 1), STORAGE_BACKOFF_MAX_MS);
}

/** Timer entry point — respects local-storage backoff. */
export function runIntervalPass(): Promise<void> | void {
  if (storageBackoffUntil && Date.now() < storageBackoffUntil) return;
  // 1.0.x — after a failed storage recovery, the background poll only probes
  // once per shared backoff window; foreground/online resume it immediately.
  if (!shouldRunBackgroundStorageWork()) return;
  return processQueue();
}

async function processQueue(): Promise<void> {
  if (processing) {
    if (processingStartedAt && Date.now() - processingStartedAt > PROCESSING_STALL_MS) {
      console.warn(
        `[SyncEngine] Watchdog: previous pass stalled for ${Math.round((Date.now() - processingStartedAt) / 1000)}s — releasing lock`
      );
      processing = false;
    } else {
      return;
    }
  }
  if (isKnownOffline()) return;

  processing = true;
  processingStartedAt = Date.now();
  const myGeneration = ++passGeneration;
  status = "syncing";
  
  try {
    await rehydrateCompletionFallbacks();

    const ready = await syncQueue.getReadySummaries();
    const total = await syncQueue.pendingCount();
    if (total > 0) {
      syncEvent("pass_started", { ready: ready.length, queueSize: total });
      logOfflineEvent("queue_replay_started", { store: "sync_queue", count: ready.length, queueSize: total });
    }
    // Local storage reads succeeded — clear any storage-failure backoff.
    storageFailureStreak = 0;
    storageBackoffUntil = 0;
    if (ready.length === 0) {
      status = total > 0 ? "syncing" : "idle";
      notify(total);
      return;
    }

    const sorted = sortForAtomicUpload(ready);
    notify(sorted.length);
    console.log(`[SyncEngine] Processing ${sorted.length} queued items (atomic order)…`);

    const failedJobAssets = new Set<string>();
    const completedJobs = new Set<string>();

    for (const item of sorted) {
      if (myGeneration !== passGeneration) {
        console.warn("[SyncEngine] Pass superseded by watchdog — stopping before next item");
        return;
      }
      const isCompletion = item.type === "COMPLETE_POC" || item.type === "COMPLETE_POD";
      if (isCompletion && failedJobAssets.has(item.job_id)) {
        if (item.attempts < MAX_ASSET_DEFER_ATTEMPTS) {
          console.log(`[SyncEngine] Deferring ${item.type} for job ${item.job_id} (assets not uploaded yet, attempt ${item.attempts + 1}/${MAX_ASSET_DEFER_ATTEMPTS})`);
          syncEvent("replay_deferred", { jobId: item.job_id, type: item.type, attempts: item.attempts });
          await syncQueue.markRetry(item.id, "Waiting for asset uploads to complete");
          continue;
        }
        console.warn(`[SyncEngine] Max asset-defer reached for ${item.type} job ${item.job_id} — proceeding with completion anyway`);
      }

      // Read this one item (with its payload) in a fresh transaction just before
      // processing. Gone → another pass already completed/discarded it: skip.
      const full = await syncQueue.get(item.id);
      if (!full) continue;

      const itemStartedAt = Date.now();
      try {
        console.log(`[SyncEngine] → start ${item.type} id=${item.id.slice(0, 8)} job=${item.job_id.slice(0, 8)} attempts=${item.attempts}`);
        if (isCompletion) {
          syncEvent("replay_started", { jobId: item.job_id, type: item.type, attempts: item.attempts });
        }
        const response = await withItemDeadline(processItem(full), `${item.type} ${item.id.slice(0, 8)}`);
        await syncQueue.delete(item.id);
        if (isCompletion) {
          syncEvent("replay_succeeded", {
            jobId: item.job_id,
            type: item.type,
            duration_ms: Date.now() - itemStartedAt,
            serverResponse: response ? JSON.stringify(response).slice(0, 300) : null,
          });
          syncEvent("queue_item_removed", { jobId: item.job_id, type: item.type });
          completedJobs.add(item.job_id);
        }
        console.log(`[SyncEngine] ✓ ${item.type} for job ${item.job_id} in ${Date.now() - itemStartedAt}ms`);
      } catch (err: any) {
        const msg = err?.message || String(err);
        console.warn(`[SyncEngine] ✗ ${item.type} for job ${item.job_id} after ${Date.now() - itemStartedAt}ms: ${msg}`);
        if (isCompletion) {
          syncEvent("replay_failed", {
            jobId: item.job_id,
            type: item.type,
            attempts: item.attempts,
            duration_ms: Date.now() - itemStartedAt,
            error: msg,
          }, true);
        }

        // ── PERMANENT FAILURE DETECTION ──
        // 404 = job deleted, 403 = not assigned — these will NEVER succeed.
        // Discard them instead of retrying forever.
        const isPermanent = msg.includes("404") || msg.includes("Job not found")
          || msg.includes("403") || msg.includes("Not assigned") || msg.includes("Not a member");
        if (isPermanent) {
          console.warn(`[SyncEngine] Permanent failure for ${item.type} job ${item.job_id} — discarding`);
          syncEvent("queue_item_discarded", { jobId: item.job_id, type: item.type, error: msg }, true);
          await syncQueue.delete(item.id);
          continue;
        }

        await syncQueue.markRetry(item.id, msg);

        if (item.type === "UPLOAD_PHOTO" || item.type === "UPLOAD_SIGNATURE") {
          failedJobAssets.add(item.job_id);
        }
      }
    }

    for (const jobId of completedJobs) {
      await refreshAfterCompletion(jobId);
    }

    await appState.setNonEssential("last_sync_time", String(Date.now()));

    const remaining = await syncQueue.pendingCount();
    logOfflineEvent("queue_replay_completed", {
      store: "sync_queue",
      count: sorted.length,
      remaining,
      completedJobs: completedJobs.size,
    });
    status = remaining > 0 ? "syncing" : "idle";
    notify(remaining);
  } catch (err) {
    console.error("[SyncEngine] Queue processing error:", err);
    if (isRecoverableDbError(err)) {
      storageFailureStreak += 1;
      const backoffMs = storageBackoffDelay(storageFailureStreak);
      storageBackoffUntil = Date.now() + backoffMs;
      captureError("sync.queue_processing", err, {
        metadata: {
          storage_recoverable: true,
          storage_failure_streak: storageFailureStreak,
          backoff_ms: backoffMs,
          ...describeDbError(err),
        },
      });
    } else {
      captureError("sync.queue_processing", err);
    }
    status = "idle";
    notify(0);
  } finally {
    if (myGeneration === passGeneration) {
      processing = false;
      processingStartedAt = 0;
    }
  }

}


async function processItem(item: SyncQueueItem): Promise<any> {
  switch (item.type) {
    case "UPSERT_POC":
      await callEdgeFunction("mobile-poc-upsert", JSON.parse(item.payload_json));
      break;
    case "UPSERT_POD":
      await callEdgeFunction("mobile-pod-upsert", JSON.parse(item.payload_json));
      break;
    case "UPSERT_PART_INSPECTION": {
      // Per-part inspection answers captured offline. Idempotent upsert on
      // (appraisal_id, part_name) — replaying is always safe.
      const { supabase } = await import("@/integrations/supabase/client");
      const payload = JSON.parse(item.payload_json);
      // D2 (2525b531): a snapshot carrying its photo base is merged with the
      // current row so later queue-reconciled links are not erased. Snapshots
      // without a base (older builds) keep the previous verbatim behaviour.
      const { PART_PHOTO_BASE_KEY, mergeReplayedPartPhotos } = await import("@/lib/partPhotoAssociation");
      const base = payload[PART_PHOTO_BASE_KEY];
      delete payload[PART_PHOTO_BASE_KEY];
      const { isPathTombstoned } = await import("@/lib/appraisalPhotoCancellation");
      const { hasPendingPhotoReferenceCleanup } = await import("@/lib/appraisalPhotoReferenceCleanupQueue");
      const isDeletedPath = (path: string) =>
        isPathTombstoned(payload.appraisal_id, path) || hasPendingPhotoReferenceCleanup(payload.appraisal_id, path);
      if (Array.isArray(base) && Array.isArray(payload.photo_urls) && payload.appraisal_id && payload.part_name) {
        const { data: row, error: readError } = await supabase
          .from("vehicle_part_inspections")
          .select("photo_urls")
          .eq("appraisal_id", payload.appraisal_id)
          .eq("part_name", payload.part_name)
          .maybeSingle();
        if (readError) throw new Error(readError.message);
        payload.photo_urls = mergeReplayedPartPhotos(
          payload.photo_urls,
          base,
          Array.isArray(row?.photo_urls) ? row.photo_urls : [],
          isDeletedPath,
        );
      } else if (Array.isArray(payload.photo_urls) && payload.appraisal_id) {
        // Older snapshot (no base): verbatim as before, minus paths deliberately deleted on this device.
        payload.photo_urls = payload.photo_urls.filter((p: unknown) => typeof p !== "string" || !isDeletedPath(p));
      }
      const { error } = await supabase
        .from("vehicle_part_inspections")
        .upsert(payload, { onConflict: "appraisal_id,part_name", ignoreDuplicates: false });
      if (error) throw new Error(error.message);
      break;
    }

    case "UPLOAD_SIGNATURE": {
      const payload = JSON.parse(item.payload_json);
      if (item.file_data) {
        payload.signatureData = item.file_data;
      }
      await callEdgeFunction("mobile-signature-upload", payload, ASSET_UPLOAD_TIMEOUT_MS);
      break;
    }
    case "UPLOAD_PHOTO": {
      const payload = JSON.parse(item.payload_json);
      if (item.file_data) {
        payload.photoData = item.file_data;
      }
      await callEdgeFunction("mobile-photo-upload", payload, ASSET_UPLOAD_TIMEOUT_MS);
      break;
    }
    case "COMPLETE_POC":
      return await callEdgeFunction("mobile-poc-complete", JSON.parse(item.payload_json));
    case "COMPLETE_POD":
      return await callEdgeFunction("mobile-pod-complete", JSON.parse(item.payload_json));
    default:
      throw new Error(`Unknown sync type: ${item.type}`);
  }
}

/** Photos/signatures are large base64 payloads on slow mobile links. */
const ASSET_UPLOAD_TIMEOUT_MS = 120_000;

async function callEdgeFunction(name: string, body: Record<string, unknown>, timeoutMs?: number): Promise<any> {
  const { data, error } = await invokeEdgeFunction(name, body, timeoutMs ? { timeoutMs } : undefined);
  if (error) throw new Error(error.message);
  return data;
}


/**
 * Clear the legacy "transportmate_conflicts" IndexedDB database.
 * Old builds may have stale conflict entries that would show up
 * if a user downgrades or if PendingUploads still references the store.
 * This runs once on engine start and silently deletes the entire DB.
 */
function clearLegacyConflictStore() {
  try {
    const req = indexedDB.deleteDatabase("transportmate_conflicts");
    req.onsuccess = () => console.log("[SyncEngine] Legacy conflict store cleared");
    req.onerror = () => {}; // ignore
  } catch {
    // ignore
  }
}
